import React, { useState } from "react";
import {
  Button,
  Box,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  Grid,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import Header from "../../components/Header";
import * as XLSX from "xlsx";
import { tokens } from "../../theme";
import { useTheme } from "@mui/material";
import { format, isValid } from "date-fns";
import { useNavigate } from "react-router-dom";
import { iepaProd } from "../../constants/global";
import './Upload.css';

const Upload = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [username, setUsername] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("");

  const locations = [
    { code: "R1", name: "Room No: 1" },
    { code: "R2", name: "Room No: 2" },
    { code: "SR1", name: "Store Room: 1" },
    { code: "SR2", name: "Store Room: 2" },
  ];

  const handleFileUpload = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
    }
  };

  const handleUploadClick = () => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }

    setLoading(true);

    const reader = new FileReader();
    const fileExtension = file.name.split(".").pop().toLowerCase();

    reader.onload = async (event) => {
      try {
        let jsonData;

        if (fileExtension === "csv") {
          const csvText = event.target.result;
          const workbook = XLSX.read(csvText, { type: "string" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          jsonData = XLSX.utils.sheet_to_json(worksheet);
        } else if (["xlsx", "xls"].includes(fileExtension)) {
          const binaryStr = event.target.result;
          const workbook = XLSX.read(binaryStr, { type: "binary" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          jsonData = XLSX.utils.sheet_to_json(worksheet);
        } else {
          throw new Error("Unsupported file format");
        }

        const dynamicColumns = Object.keys(jsonData[0] || {}).map((key) => ({
          field: key,
          headerName: key.charAt(0).toUpperCase() + key.slice(1),
          width: 150,
        }));

        const rowsWithId = jsonData.map((row, index) => {
          const formattedRow = { ...row };
          Object.keys(formattedRow).forEach((key) => {
            const value = formattedRow[key];
            if (isValid(new Date(value))) {
              formattedRow[key] = format(new Date(value), "dd-MM-yyyy");
            }
          });
          return { ...formattedRow, id: index + 1 };
        });

        setColumns([
          ...dynamicColumns,
          {
            field: "view",
            headerName: "View",
            width: 150,
            renderCell: (params) => (
              <Button
                variant="contained"
                color="success"
                onClick={() => handleView(params.row)}
              >
                View
              </Button>
            ),
          },
          {
            field: "delete",
            headerName: "Delete",
            width: 150,
            renderCell: (params) => (
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(params.row.id)}
              >
                Delete
              </Button>
            ),
          },
        ]);

        setRows(rowsWithId);

        const formData = new FormData();
        formData.append("file", file);

        const response = await fetch(
          `${iepaProd.iepaCollectionSrvAPI}/collection/interface/upload/csv/org/1`,
          { method: "POST", body: formData }
        );

        if (!response.ok) throw new Error("Upload failed");

        const result = await response.json();
        console.log("Upload successful:", result);
        alert("File uploaded successfully!");
      } catch (error) {
        console.error("Error:", error);
        alert("Failed to process or upload file.");
      } finally {
        setLoading(false);
      }
    };

    if (fileExtension === "csv") reader.readAsText(file);
    else reader.readAsBinaryString(file);
  };

  const handleView = (row) => {
    const requestNumber = row["Collection Request Number"];

    if (!requestNumber) {
      console.error("Collection Request Number missing:", row);
      return;
    }

    // Remove all hyphens
    const cleaned = requestNumber.replaceAll("-", "");

    navigate(`/view-data/${cleaned}`);
  };
  const handleDelete = (id) => setRows((prev) => prev.filter((r) => r.id !== id));

  return (
    <Box sx={{ padding: 2 }}>
      <Header title="Collection Interface" subtitle="" />

      {/* Form Section */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Username"
            variant="outlined"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            fullWidth
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Choose Location</InputLabel>
            <Select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              label="Choose Location"
            >
              {locations.map((loc) => (
                <MenuItem key={loc.code} value={loc.code}>
                  {loc.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* File Upload Section */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Grid item xs={12} sm="auto">
          <input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileUpload}
          />
          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              sx={{
                backgroundColor: colors.blueAccent[700],
                color: colors.primary[400],
                fontWeight: "bold",
              }}
            >
              Choose File
            </Button>
          </label>
        </Grid>

        <Grid item xs={12} sm="auto">
          {fileName && (
            <Typography
              sx={{
                maxWidth: 200,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {fileName}
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} sm="auto">
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.primary[400],
              fontWeight: "bold",
            }}
            onClick={handleUploadClick}
            disabled={!file}
          >
            Upload
          </Button>
        </Grid>
      </Grid>

      {/* DataGrid */}
      <Box
        sx={{
          height: "78vh",
          "& .MuiDataGrid-root": { border: "none" },
          "& .MuiDataGrid-cell": { borderBottom: "1px solid grey", wordWrap: "break-word" },
          "& .MuiDataGrid-columnHeaders": { backgroundColor: colors.blueAccent[700], borderBottom: "none" },
          "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
          "& .MuiDataGrid-footerContainer": { borderTop: "none", backgroundColor: colors.blueAccent[900], height: "5vh" },
          "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": { color: `${colors.grey[100]} !important` },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          checkboxSelection={rows.length > 0}
          loading={loading}
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          components={{ Toolbar: GridToolbar }}
          sx={{ width: "100%" }}
        />
      </Box>
    </Box>
  );
};

export default Upload;
